import type { MonthlyReport, MonthAttachment, Profile, TimeEntryAudit } from '../types';
import type { ReleaseNote } from './releaseNotes';
import { format, parseISO } from 'date-fns';

export type NotificationType =
  | 'release'
  | 'month_completed'
  | 'month_approved'
  | 'document_uploaded'
  | 'supervisor_time_edit';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  isNew: boolean;
  linkView?: 'dashboard' | 'supervisor';
  linkMonth?: string;
  linkUserId?: string;
  metadata?: {
    version?: string;
    url?: string;
    fileName?: string;
    fileUrl?: string;
    userName?: string;
    month?: string;
    userId?: string;
    changesCount?: number;
    changes?: ReleaseNote['changes'];
  };
}

export interface BuildNotificationsParams {
  releaseNotes: ReleaseNote[];
  monthlyReports: MonthlyReport[];
  monthAttachments: MonthAttachment[];
  timeEntryAudits?: TimeEntryAudit[];
  currentUserId?: string;
  isSupervisor: boolean;
  profiles?: Profile[];
  lastViewedAt: string | null;
}

export function getUserDisplayName(profile?: Profile | null, fallbackId?: string): string {
  if (profile) {
    const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    if (fullName) return fullName;
    if (profile.email) return profile.email;
    if (profile.initials) return profile.initials;
  }
  if (fallbackId) {
    return fallbackId.length > 8 ? `User ${fallbackId.slice(0, 8)}` : fallbackId;
  }
  return 'A user';
}

function isNotificationNew(timestamp: string, lastViewedAt: string | null): boolean {
  if (!lastViewedAt) return true;
  const time = new Date(timestamp).getTime();
  const lastViewed = new Date(lastViewedAt).getTime();
  if (isNaN(time) || isNaN(lastViewed)) return false;
  return time > lastViewed;
}

function buildReleaseNotifications(
  releaseNotes: ReleaseNote[],
  lastViewedAt: string | null
): AppNotification[] {
  return releaseNotes.map((release, idx) => {
    let summary = release.changes.length > 0
      ? release.changes.slice(0, 2).map(c => c.text).join(' • ')
      : 'New release update available';
    if (release.changes.length > 2) {
      summary += ` (+${release.changes.length - 2} more)`;
    }

    const title = release.summaryTitle
      ? `JaTT v${release.version}: ${release.summaryTitle}`
      : `JaTT v${release.version}`;

    return {
      id: `release-${release.version}-${release.date || idx}`,
      type: 'release' as const,
      title,
      message: summary,
      timestamp: release.timestamp,
      isNew: isNotificationNew(release.timestamp, lastViewedAt),
      metadata: {
        version: release.version,
        url: release.url,
        changesCount: release.changes.length,
        changes: release.changes
      }
    };
  });
}

function buildMonthlyReportNotifications(
  monthlyReports: MonthlyReport[],
  currentUserId: string | undefined,
  isSupervisor: boolean,
  profileMap: Map<string, Profile>,
  lastViewedAt: string | null
): AppNotification[] {
  const notifications: AppNotification[] = [];

  for (const report of monthlyReports) {
    // 1. Supervisor notification: User completed a month
    if (isSupervisor && report.completed && report.completedAt) {
      const user = profileMap.get(report.userId);
      const userName = getUserDisplayName(user, report.userId);

      notifications.push({
        id: `month-completed-${report.userId}-${report.month}`,
        type: 'month_completed',
        title: 'Month Submitted',
        message: `${userName} submitted monthly report for ${report.month}`,
        timestamp: report.completedAt,
        isNew: isNotificationNew(report.completedAt, lastViewedAt),
        metadata: {
          userName,
          month: report.month,
          userId: report.userId
        }
      });
    }

    // 2. User notification: Supervisor approved a month
    if (currentUserId && report.userId === currentUserId && report.reviewed && report.reviewedAt) {
      notifications.push({
        id: `month-approved-${report.userId}-${report.month}`,
        type: 'month_approved',
        title: 'Report Approved',
        message: `Your monthly report for ${report.month} was approved by supervisor`,
        timestamp: report.reviewedAt,
        isNew: isNotificationNew(report.reviewedAt, lastViewedAt),
        metadata: {
          month: report.month
        }
      });
    }
  }

  return notifications;
}

function buildAttachmentNotifications(
  monthAttachments: MonthAttachment[],
  isSupervisor: boolean,
  profileMap: Map<string, Profile>,
  lastViewedAt: string | null
): AppNotification[] {
  if (!isSupervisor) return [];

  return monthAttachments
    .filter(att => Boolean(att.createdAt))
    .map(att => {
      const user = profileMap.get(att.userId);
      const userName = getUserDisplayName(user, att.userId);

      return {
        id: `doc-uploaded-${att.id}`,
        type: 'document_uploaded' as const,
        title: 'Document Uploaded',
        message: `${userName} uploaded "${att.fileName}" for ${att.month}`,
        timestamp: att.createdAt,
        isNew: isNotificationNew(att.createdAt, lastViewedAt),
        linkView: 'supervisor',
        linkMonth: att.month,
        linkUserId: att.userId,
        metadata: {
          userName,
          fileName: att.fileName,
          fileUrl: att.onedriveWebUrl || undefined,
          month: att.month,
          userId: att.userId
        }
      };
    });
}

function formatAuditEntryDate(rawDate?: unknown): { formatted: string; month?: string } {
  if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
    const month = rawDate.slice(0, 7);
    try {
      return { formatted: format(parseISO(rawDate), 'dd.MM.yyyy'), month };
    } catch {
      return { formatted: rawDate, month };
    }
  }
  return { formatted: 'a past date' };
}

function buildTimeEntryAuditNotifications(
  timeEntryAudits: TimeEntryAudit[] = [],
  currentUserId: string | undefined,
  profileMap: Map<string, Profile>,
  lastViewedAt: string | null
): AppNotification[] {
  if (!currentUserId) return [];

  return timeEntryAudits
    .filter(audit => audit.targetUserId === currentUserId && audit.changedBy !== currentUserId)
    .map(audit => {
      const supervisor = profileMap.get(audit.changedBy);
      const supervisorName = getUserDisplayName(supervisor, audit.changedBy);

      const entryData = (audit.newData || audit.oldData) as { date?: string } | null;
      const { formatted: dateStr, month: monthStr } = formatAuditEntryDate(entryData?.date);

      let actionDesc = 'updated your time entry';
      if (audit.action === 'DELETE') {
        actionDesc = 'deleted your time entry';
      } else if (audit.action === 'INSERT') {
        actionDesc = 'created a time entry for you';
      }

      const message = `${supervisorName} ${actionDesc} for ${dateStr}.`;

      return {
        id: `audit-${audit.id}`,
        type: 'supervisor_time_edit' as const,
        title: 'Time Entry Modified',
        message,
        timestamp: audit.createdAt,
        isNew: isNotificationNew(audit.createdAt, lastViewedAt),
        metadata: {
          userName: supervisorName,
          month: monthStr,
          userId: audit.targetUserId
        }
      };
    });
}

export function buildNotifications({
  releaseNotes,
  monthlyReports,
  monthAttachments,
  timeEntryAudits = [],
  currentUserId,
  isSupervisor,
  profiles = [],
  lastViewedAt
}: BuildNotificationsParams): AppNotification[] {
  const profileMap = new Map<string, Profile>();
  profiles.forEach(p => profileMap.set(p.id, p));

  const releaseNotifs = buildReleaseNotifications(releaseNotes, lastViewedAt);
  const reportNotifs = buildMonthlyReportNotifications(monthlyReports, currentUserId, isSupervisor, profileMap, lastViewedAt);
  const attachmentNotifs = buildAttachmentNotifications(monthAttachments, isSupervisor, profileMap, lastViewedAt);
  const auditNotifs = buildTimeEntryAuditNotifications(timeEntryAudits, currentUserId, profileMap, lastViewedAt);

  const combined = [...releaseNotifs, ...reportNotifs, ...attachmentNotifs, ...auditNotifs];

  // Sort descending by timestamp (newest first)
  combined.sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeB - timeA;
  });

  return combined;
}
